import { z } from 'zod';
import {
  BlockTypeEnum,
  ExperienceTypeEnum,
  StepTypeEnum,
} from '../taxonomy';
import { JsonValueSchema } from './common';
import { BlockEventsSchema } from './ui-events';

export const PageContentLayoutSchema = z.object({
  columns: z.number().int().positive().optional(),
  row_gap: z.number().nonnegative().optional(),
  column_gap: z.number().nonnegative().optional(),
});

export const PageBlockLayoutSchema = z.object({
  column: z.number().int().nonnegative().optional(),
  row: z.number().int().nonnegative().optional(),
  column_span: z.number().int().positive().optional(),
  row_span: z.number().int().positive().optional(),
  order: z.number().int().nonnegative().optional(),
});

export const PageBlockSchema = z.object({
  id: z.string().min(1, 'Block ID must not be empty'),
  type: BlockTypeEnum,
  props: z.record(JsonValueSchema),
  layout: PageBlockLayoutSchema.optional(),
  events: BlockEventsSchema.optional(),
});

export const PageContentMetaSchema = z.object({
  step_type: StepTypeEnum,
  experience_type: ExperienceTypeEnum,
  estimated_scroll_depth: z.string().min(1),
  conversion_goal: z.string().min(1),
});

const CTA_BLOCK_TYPES = ['cta-button-group', 'button'] as const;

export const PageContentSchema = z
  .object({
    blocks: z.array(PageBlockSchema).min(1),
    meta: PageContentMetaSchema,
    version: z.number().int().positive().optional(),
    layout: PageContentLayoutSchema.optional(),
  })
  .refine(
    (data) =>
      data.blocks.some((block) =>
        (CTA_BLOCK_TYPES as readonly string[]).includes(block.type),
      ),
    {
      message:
        "Screen must contain at least one CTA block ('cta-button-group' or 'button')",
    },
  );

export interface PageContentValidationResult {
  valid: boolean;
  errors: string[];
  data: ValidatedPageContent | null;
}

export function validatePageContent(
  content: Record<string, unknown>,
): PageContentValidationResult {
  const result = PageContentSchema.safeParse(content);

  if (result.success) {
    return { valid: true, errors: [], data: result.data };
  }

  return {
    valid: false,
    errors: result.error.issues.map((issue) => {
      const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
      return `${path}${issue.message}`;
    }),
    data: null,
  };
}

export type ValidatedPageContentLayout = z.infer<
  typeof PageContentLayoutSchema
>;
export type ValidatedPageBlockLayout = z.infer<typeof PageBlockLayoutSchema>;
export type ValidatedPageBlock = z.infer<typeof PageBlockSchema>;
export type ValidatedPageContentMeta = z.infer<typeof PageContentMetaSchema>;
export type ValidatedPageContent = z.infer<typeof PageContentSchema>;
